import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import { useResultsArchiveQuery, useHistoricalResultsJWTQuery } from '@/api/vercel';
import ResultsArchiveTable from '@/components/ResultsArchiveTable';
import BettingStrategies from '@/components/BettingStrategies';
import ROIComparisonText from '@/components/ROIComparisonText';
import TrackEventsAccordion from '@/components/TrackEventsAccordion';
import { format } from 'date-fns';
import { LockIcon } from 'lucide-react';
import { isSessionValid } from '@/utils/auth';
import AuthModal from '@/components/auth-modal';
import trackDetails from '/public/data/horseracing/tracks/track-details.json';
import trackEvents from '/public/data/horseracing/tracks/track-events.json';

// Owner, 30 Oct 2026: hide profit, Max Hedge and the betting-strategy panels on
// track pages. Set to true to bring them back.
const SHOW_PROFIT_AND_HEDGE = false;

export default function TrackResults() {
  const { state, trackName } = useParams();
  const [trackData, setTrackData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [showMaxHedge, setShowMaxHedge] = useState(false);
  const [maxHedgeWinRate, setMaxHedgeWinRate] = useState(0.8);

  // Max hedge calculation functions
  const calculateMorningMaxHedge = (earlyTips) => {
    if (!Array.isArray(earlyTips)) return '-';

    let reciprocalSum = 0;
    earlyTips.forEach(tip => {
      if (tip?.formattedName) {
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (odds && !isNaN(odds) && odds > 0) {
          reciprocalSum += 1 / parseFloat(odds);
        }
      }
    });

    return reciprocalSum > 0 ? reciprocalSum.toFixed(3) : '-';
  };

  const calculateRaceTimeMaxHedge = (raceTips) => {
    if (!Array.isArray(raceTips)) return '-';

    let reciprocalSum = 0;
    raceTips.forEach(tip => {
      if (tip?.formattedName) {
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (odds && !isNaN(odds) && odds > 0) {
          reciprocalSum += 1 / parseFloat(odds);
        }
      }
    });

    return reciprocalSum > 0 ? reciprocalSum.toFixed(3) : '-';
  };

  const calculateCombinedMaxHedge = (earlyTips, raceTips) => {
    if (!Array.isArray(earlyTips) || !Array.isArray(raceTips)) return '-';

    const uniqueTips = new Map();

    // First add morning tips
    earlyTips.forEach(tip => {
      if (tip?.formattedName) {
        const horseNumber = tip.formattedName.split('.')[0];
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (horseNumber !== '-' && odds && !isNaN(odds) && odds > 0) {
          uniqueTips.set(horseNumber, {
            odds: parseFloat(odds),
            source: 'morning'
          });
        }
      }
    });

    // Then add race-time tips (will override morning tips if same horse)
    raceTips.forEach(tip => {
      if (tip?.formattedName) {
        const horseNumber = tip.formattedName.split('.')[0];
        const odds = tip.lastPriceTraded || tip.currentPrice;
        if (horseNumber !== '-' && odds && !isNaN(odds) && odds > 0) {
          uniqueTips.set(horseNumber, {
            odds: parseFloat(odds),
            source: 'raceTime'
          });
        }
      }
    });

    if (uniqueTips.size === 0) return '-';

    // Calculate reciprocal sum
    let reciprocalSum = 0;
    uniqueTips.forEach(tipData => {
      reciprocalSum += 1 / tipData.odds;
    });

    return reciprocalSum.toFixed(3);
  };

  // Helper function to check if any tips in a set won the race
  const checkTipsWon = (tips, winnerSelectionId) => {
    if (!Array.isArray(tips) || !winnerSelectionId) return { won: false, winningTip: null };

    const winningTip = tips.find(tip => tip?.selectionId === winnerSelectionId);
    return { won: !!winningTip, winningTip };
  };

  const normalizeSelectionId = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str.length ? str : null;
  };

  const normalizeHorseNumber = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str.length) return null;
    const withoutLeadingZeros = str.replace(/^0+/, '');
    return withoutLeadingZeros.length ? withoutLeadingZeros : '0';
  };

  const extractHorseNumber = (entry) => {
    if (!entry) return null;

    const nameFields = [
      entry.formattedName,
      entry.selectionName,
      entry.runnerName,
      entry.name
    ];

    for (const field of nameFields) {
      if (typeof field === 'string' && field.trim().length) {
        const leadingNumberMatch = field.match(/^(\d+)[\s.\-_:]/);
        const candidate = leadingNumberMatch ? leadingNumberMatch[1] : field.split('.')[0];
        const normalized = normalizeHorseNumber(candidate);
        if (normalized) return normalized;
      }
    }

    const fallbackFields = [
      entry.horseNumber,
      entry.runnerNumber,
      entry.raceNumber,
      entry.number
    ];

    for (const field of fallbackFields) {
      const normalized = normalizeHorseNumber(field);
      if (normalized) return normalized;
    }

    return null;
  };

  const normalizeRaceNumber = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    const numeric = parseInt(str, 10);
    if (Number.isFinite(numeric)) {
      return String(numeric);
    }
    return str;
  };

  const getRaceNumberFromRace = (race) => {
    if (!race) return null;

    if (race.raceNumber !== null && race.raceNumber !== undefined) {
      return normalizeRaceNumber(race.raceNumber);
    }

    if (race.race !== null && race.race !== undefined) {
      return normalizeRaceNumber(race.race);
    }

    if (typeof race.id === 'string') {
      const parts = race.id.split('-');
      if (parts.length >= 4) {
        return normalizeRaceNumber(parts[parts.length - 4]);
      }
    }

    return null;
  };

const parseToIsoDate = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    if (!Number.isNaN(date.valueOf())) {
      return date.toISOString().slice(0, 10);
    }
  }

  if (typeof value === 'object' && value !== null) {
    if (typeof value._seconds === 'number') {
      const millis = (value._seconds * 1000) + (value._nanoseconds ? value._nanoseconds / 1e6 : 0);
      const date = new Date(millis);
      if (!Number.isNaN(date.valueOf())) {
        return date.toISOString().slice(0, 10);
      }
    }

    if (typeof value.seconds === 'number') {
      const millis = (value.seconds * 1000) + (value.nanoseconds ? value.nanoseconds / 1e6 : 0);
      const date = new Date(millis);
      if (!Number.isNaN(date.valueOf())) {
        return date.toISOString().slice(0, 10);
      }
    }
  }

  const str = String(value).trim();
  if (!str) return null;

  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const dmyMatch = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return `${year}-${month}-${day}`;
  }

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.valueOf())) {
    return parsed.toISOString().slice(0, 10);
  }

  return null;
};

const getRaceDateKey = (race) => {
  if (!race) return null;

  const candidates = [
    race.date,
    race.raceDate,
    race.race_date,
    race.meetingDate,
    race.meeting_date,
    race.raceStartTime,
    race.raceStart,
    race.startTime,
    race.start_time,
    race.startTimeUtc,
    race.start_time_utc,
    race.betStartTime,
    race.bet_start_time,
    race.betfairResults?.raceDate,
    race.betfairResults?.raceStartTime,
    race.betfairResults?.raceStart,
    race.betfairResults?.startTime,
    race.betfairResults?.start_time
  ];

  for (const candidate of candidates) {
    const key = parseToIsoDate(candidate);
    if (key) return key;
  }

  if (race.betfairResults?.winner?.settledTime) {
    const key = parseToIsoDate(race.betfairResults.winner.settledTime);
    if (key) return key;
  }

  if (race.archivedAt) {
    const key = parseToIsoDate(race.archivedAt);
    if (key) return key;
  }

  return null;
};

const buildFeatureEventKey = (dateKey, raceNumberKey) => {
  if (!dateKey || !raceNumberKey) return null;
  return `${dateKey}|${raceNumberKey}`;
};

  // Get results archive data for the track
  // Keep hyphenated format for API calls, but create display name with spaces
  const apiTrackName = trackName || '';
  const displayTrackName = trackName ? trackName.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ') : '';

  // Only fetch archive data if user is authenticated
  const isAuthenticated = isSessionValid();
  const {
    data: archiveData,
    isLoading: isArchiveLoading,
    error: archiveError
  } = useResultsArchiveQuery({
    action: "get_results",
    trackName: apiTrackName,
    enabled: !!apiTrackName
  });

  // Calculate max hedge summary for qualifying races
  const maxHedgeSummary = useMemo(() => {
    if (!archiveData?.data) return {
      morning: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 },
      raceTime: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 },
      combined: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 }
    };

    // Filter out races that lack BOTH earlyPredictionData.firstFourData AND predictionData.firstFourData
    const allRaces = Array.isArray(archiveData.data) ? archiveData.data : [];
    const races = allRaces.filter((race) => {
      const hasEarlyData = race.earlyPredictionData?.firstFourData &&
        Array.isArray(race.earlyPredictionData.firstFourData) &&
        race.earlyPredictionData.firstFourData.length > 0;

      const hasPredictionData = race.predictionData?.firstFourData &&
        Array.isArray(race.predictionData.firstFourData) &&
        race.predictionData.firstFourData.length > 0;

      // Include race if it has either early data OR prediction data
      return hasEarlyData || hasPredictionData;
    });

    // Morning qualifying races
    const morningQualifying = races.filter(race => {
      const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
      const morningMaxHedge = parseFloat(calculateMorningMaxHedge(earlyTips));
      const winner = race.betfairResults?.winner;
      const { won } = checkTipsWon(earlyTips, winner?.selectionId);

      return !isNaN(morningMaxHedge) && morningMaxHedge > 0 && morningMaxHedge < maxHedgeWinRate && won;
    });

    const morningTotalWon = morningQualifying.reduce((sum, race) => {
      const winner = race.betfairResults?.winner;
      const winPrice = parseFloat(winner?.startingPrice) || 0;
      return sum + winPrice;
    }, 0);
    const morningTotalWagered = morningQualifying.length * 4; // 4 tips per race (T1-T4)
    const morningNetReturn = morningTotalWon - morningTotalWagered;
    const morningROI = morningTotalWagered > 0 ? (morningNetReturn / morningTotalWagered) * 100 : 0;

    // Race-time qualifying races
    const raceTimeQualifying = races.filter(race => {
      const raceTips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
      const raceTimeMaxHedge = parseFloat(calculateRaceTimeMaxHedge(raceTips));
      const winner = race.betfairResults?.winner;
      const { won } = checkTipsWon(raceTips, winner?.selectionId);

      return !isNaN(raceTimeMaxHedge) && raceTimeMaxHedge > 0 && raceTimeMaxHedge < maxHedgeWinRate && won;
    });

    const raceTimeTotalWon = raceTimeQualifying.reduce((sum, race) => {
      const winner = race.betfairResults?.winner;
      const winPrice = parseFloat(winner?.startingPrice) || 0;
      return sum + winPrice;
    }, 0);
    const raceTimeTotalWagered = raceTimeQualifying.length * 4; // 4 tips per race (T5-T8)
    const raceTimeNetReturn = raceTimeTotalWon - raceTimeTotalWagered;
    const raceTimeROI = raceTimeTotalWagered > 0 ? (raceTimeNetReturn / raceTimeTotalWagered) * 100 : 0;

    // Combined qualifying races
    const combinedQualifying = races.filter(race => {
      const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
      const raceTips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
      const combinedMaxHedge = parseFloat(calculateCombinedMaxHedge(earlyTips, raceTips));
      const winner = race.betfairResults?.winner;
      const allTips = [...earlyTips, ...raceTips];
      const { won } = checkTipsWon(allTips, winner?.selectionId);

      return !isNaN(combinedMaxHedge) && combinedMaxHedge > 0 && combinedMaxHedge < maxHedgeWinRate && won;
    });

    const combinedTotalWon = combinedQualifying.reduce((sum, race) => {
      const winner = race.betfairResults?.winner;
      const winPrice = parseFloat(winner?.startingPrice) || 0;
      return sum + winPrice;
    }, 0);

    // For combined strategy, calculate unique tips per race (avoiding double counting)
    const combinedTotalWagered = combinedQualifying.reduce((sum, race) => {
      const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
      const raceTips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
      const uniqueTips = new Map();

      // Add morning tips
      earlyTips.forEach(tip => {
        if (tip?.formattedName) {
          const horseNumber = tip.formattedName.split('.')[0];
          if (horseNumber !== '-') {
            uniqueTips.set(horseNumber, true);
          }
        }
      });

      // Add race-time tips (will override if same horse)
      raceTips.forEach(tip => {
        if (tip?.formattedName) {
          const horseNumber = tip.formattedName.split('.')[0];
          if (horseNumber !== '-') {
            uniqueTips.set(horseNumber, true);
          }
        }
      });

      return sum + uniqueTips.size;
    }, 0);

    const combinedNetReturn = combinedTotalWon - combinedTotalWagered;
    const combinedROI = combinedTotalWagered > 0 ? (combinedNetReturn / combinedTotalWagered) * 100 : 0;

    return {
      morning: {
        qualifyingRaces: morningQualifying.length,
        totalWon: morningTotalWon,
        totalWagered: morningTotalWagered,
        netReturn: morningNetReturn,
        roi: morningROI
      },
      raceTime: {
        qualifyingRaces: raceTimeQualifying.length,
        totalWon: raceTimeTotalWon,
        totalWagered: raceTimeTotalWagered,
        netReturn: raceTimeNetReturn,
        roi: raceTimeROI
      },
      combined: {
        qualifyingRaces: combinedQualifying.length,
        totalWon: combinedTotalWon,
        totalWagered: combinedTotalWagered,
        netReturn: combinedNetReturn,
        roi: combinedROI
      }
    };
  }, [archiveData, showMaxHedge, maxHedgeWinRate]);

  // Calculate optimal thresholds for each strategy
  const optimalThresholds = useMemo(() => {
    if (!archiveData?.data) return {
      morning: { threshold: 0.80, roi: 0, races: 0 },
      raceTime: { threshold: 0.80, roi: 0, races: 0 },
      combined: { threshold: 0.80, roi: 0, races: 0 }
    };

    // Filter out races that lack BOTH earlyPredictionData.firstFourData AND predictionData.firstFourData
    const allRaces = Array.isArray(archiveData.data) ? archiveData.data : [];
    const races = allRaces.filter((race) => {
      const hasEarlyData = race.earlyPredictionData?.firstFourData &&
        Array.isArray(race.earlyPredictionData.firstFourData) &&
        race.earlyPredictionData.firstFourData.length > 0;

      const hasPredictionData = race.predictionData?.firstFourData &&
        Array.isArray(race.predictionData.firstFourData) &&
        race.predictionData.firstFourData.length > 0;

      // Include race if it has either early data OR prediction data
      return hasEarlyData || hasPredictionData;
    });
    const thresholds = [];

    // Test thresholds from 0.10 to 10.00 in 0.05 increments
    for (let t = 0.10; t <= 10.00; t += 0.05) {
      thresholds.push(parseFloat(t.toFixed(2)));
    }

    const analyzeStrategy = (strategyType) => {
      let bestThreshold = 0.80;
      let bestROI = -Infinity;
      let bestRaces = 0;

      thresholds.forEach(threshold => {
        const qualifyingRaces = races.filter(race => {
          let tips, maxHedge, winner, won;

          if (strategyType === 'morning') {
            tips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
            maxHedge = parseFloat(calculateMorningMaxHedge(tips));
            winner = race.betfairResults?.winner;
            const result = checkTipsWon(tips, winner?.selectionId);
            won = result.won;
          } else if (strategyType === 'raceTime') {
            tips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
            maxHedge = parseFloat(calculateRaceTimeMaxHedge(tips));
            winner = race.betfairResults?.winner;
            const result = checkTipsWon(tips, winner?.selectionId);
            won = result.won;
          } else { // combined
            const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
            const raceTips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
            maxHedge = parseFloat(calculateCombinedMaxHedge(earlyTips, raceTips));
            winner = race.betfairResults?.winner;
            const allTips = [...earlyTips, ...raceTips];
            const result = checkTipsWon(allTips, winner?.selectionId);
            won = result.won;
          }

          return !isNaN(maxHedge) && maxHedge > 0 && maxHedge < threshold && won;
        });

        if (qualifyingRaces.length === 0) return;

        const totalWon = qualifyingRaces.reduce((sum, race) => {
          const winner = race.betfairResults?.winner;
          const winPrice = parseFloat(winner?.startingPrice) || 0;
          return sum + winPrice;
        }, 0);

        let totalWagered;
        if (strategyType === 'combined') {
          totalWagered = qualifyingRaces.reduce((sum, race) => {
            const earlyTips = Array.isArray(race.earlyPredictionData?.firstFourData) ? race.earlyPredictionData.firstFourData : [];
            const raceTips = Array.isArray(race.predictionData?.firstFourData) ? race.predictionData.firstFourData : [];
            const uniqueTips = new Map();

            earlyTips.forEach(tip => {
              if (tip?.formattedName) {
                const horseNumber = tip.formattedName.split('.')[0];
                if (horseNumber !== '-') {
                  uniqueTips.set(horseNumber, true);
                }
              }
            });

            raceTips.forEach(tip => {
              if (tip?.formattedName) {
                const horseNumber = tip.formattedName.split('.')[0];
                if (horseNumber !== '-') {
                  uniqueTips.set(horseNumber, true);
                }
              }
            });

            return sum + uniqueTips.size;
          }, 0);
        } else {
          totalWagered = qualifyingRaces.length * 4; // 4 tips per race
        }

        const netReturn = totalWon - totalWagered;
        const roi = totalWagered > 0 ? (netReturn / totalWagered) * 100 : 0;

        if (roi > bestROI && qualifyingRaces.length >= 3) { // Minimum 3 races for statistical significance
          bestROI = roi;
          bestThreshold = threshold;
          bestRaces = qualifyingRaces.length;
        }
      });

      return {
        threshold: bestThreshold,
        roi: bestROI,
        races: bestRaces
      };
    };

    return {
      morning: analyzeStrategy('morning'),
      raceTime: analyzeStrategy('raceTime'),
      combined: analyzeStrategy('combined')
    };
  }, [archiveData]);

  // Get historical results data using JWT authentication
  const [histResults, setHistResults] = useState({ data: {} });
  const [isHistoricalLoading, setIsHistoricalLoading] = useState(true);

  // Use the JWT-based query hook for historical results (excluding Rockhampton)
  const {
    data: jwtHistoricalData,
    isLoading: isJwtHistoricalLoading,
    error: jwtHistoricalError
  } = useHistoricalResultsJWTQuery({
    date: format(new Date(), 'dd-MM-yyyy'),
    fetchAll: false,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!trackName && trackName.toLowerCase() !== 'rockhampton' // Only enable for non-Rockhampton tracks and authenticated users
  });

  // Calculate max hedge values for the track
  const trackWithMaxHedge = useMemo(() => {
    if (!trackData) return null;

    // Calculate morning max hedge (sum of reciprocals of T1-T4 odds)
    let morningMaxHedge = 0;
    [1, 2, 3, 4].forEach(position => {
      const posData = trackData.early?.positions?.[`p${position}`];
      // Estimate odds from ROI if not available: if ROI is 150%, odds might be around 2.5
      let odds = posData?.averageOdds || posData?.odds;
      if (!odds && posData?.roi && posData.roi > 0) {
        odds = (posData.roi / 100) + 1;
      }
      if (odds && odds > 0) {
        const reciprocal = 1 / odds;
        morningMaxHedge += reciprocal;
      }
    });

    // Calculate race-time max hedge (sum of reciprocals of T5-T8 odds)
    let raceTimeMaxHedge = 0;
    [1, 2, 3, 4].forEach(position => {
      const posData = trackData.raceTime?.positions?.[`p${position}`];
      // Estimate odds from ROI if not available
      let odds = posData?.averageOdds || posData?.odds;
      if (!odds && posData?.roi && posData.roi > 0) {
        odds = (posData.roi / 100) + 1;
      }
      if (odds && odds > 0) {
        const reciprocal = 1 / odds;
        raceTimeMaxHedge += reciprocal;
      }
    });

    // Calculate combined max hedge (unique tips with bias toward race-time odds)
    const uniqueTips = new Map();

    // First add morning tips
    [1, 2, 3, 4].forEach(position => {
      const posData = trackData.early?.positions?.[`p${position}`];
      let odds = posData?.averageOdds || posData?.odds;
      if (!odds && posData?.roi && posData.roi > 0) {
        odds = (posData.roi / 100) + 1;
      }
      if (posData && odds && odds > 0) {
        const tipKey = posData.tipName || `morning-t${position}`;
        uniqueTips.set(tipKey, {
          odds,
          reciprocal: 1 / odds,
          source: 'morning'
        });
      }
    });

    // Then add race-time tips (will override morning tips if same horse)
    [1, 2, 3, 4].forEach(position => {
      const posData = trackData.raceTime?.positions?.[`p${position}`];
      let odds = posData?.averageOdds || posData?.odds;
      if (!odds && posData?.roi && posData.roi > 0) {
        odds = (posData.roi / 100) + 1;
      }
      if (posData && odds && odds > 0) {
        const tipKey = posData.tipName || `racetime-t${position + 4}`;
        uniqueTips.set(tipKey, {
          odds,
          reciprocal: 1 / odds,
          source: 'raceTime'
        });
      }
    });

    const combinedMaxHedge = Array.from(uniqueTips.values()).reduce((sum, tip) => sum + tip.reciprocal, 0);

    return {
      ...trackData,
      morningMaxHedge,
      raceTimeMaxHedge,
      combinedMaxHedge,
      uniqueTipsCount: uniqueTips.size
    };
  }, [trackData]);



  // Handle loading of historical data (including special case for Rockhampton)
  useEffect(() => {
    async function loadData() {
      try {
        if (trackName.toLowerCase() === 'rockhampton') {
          // Load static test data for Rockhampton
          const response = await fetch('/data/test/qld-rockhampton.json');
          const data = await response.json();
          setHistResults({ data: { 'rockhampton': data } });
          setIsHistoricalLoading(false);
        } else {
          // Use JWT-based query data for other tracks
          if (jwtHistoricalData) {
            setHistResults(jwtHistoricalData);
            setIsHistoricalLoading(false);
          } else if (jwtHistoricalError) {
            setIsHistoricalLoading(false);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setIsHistoricalLoading(false);
      }
    }

    // For Rockhampton, load data immediately
    if (trackName.toLowerCase() === 'rockhampton') {
      loadData();
    } else {
      // For other tracks, use the JWT query result
      setIsHistoricalLoading(isJwtHistoricalLoading);
      if (jwtHistoricalData) {
        setHistResults(jwtHistoricalData);
      }

    }
  }, [trackName, jwtHistoricalData, isJwtHistoricalLoading, jwtHistoricalError]);

  // Process track data (only if trackData hasn't been set by archive data)
  useEffect(() => {
    if (!isHistoricalLoading && histResults?.data && !trackData) {
      try {
        let trackStats;

        // Special handling for Rockhampton test data
        if (trackName.toLowerCase() === 'rockhampton' && histResults.data.rockhampton) {
          const data = histResults.data.rockhampton;
          trackStats = {
            name: data.track,
            totalRaces: data.totalRaces,
            early: data.early,
            raceTime: data.raceTime,
            recentResults: [], // No recent results in test data
            featureTipWins: {}
          };
        } else {
          // Default initialization for other tracks
          trackStats = {
            name: displayTrackName,
            totalRaces: 0,
            early: {
              positions: {
                p1: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p2: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p3: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p4: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 }
              },
              totalBets: 0,
              totalReturns: 0,
              totalProfit: 0,
              roi: 0
            },
            raceTime: {
              positions: {
                p1: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p2: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p3: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
                p4: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 }
              },
              totalBets: 0,
              totalReturns: 0,
              totalProfit: 0,
              roi: 0
            },
            recentResults: [],
            featureTipWins: {}
          };

          // Process historical data for other tracks
          Object.entries(histResults.data).forEach(([key, data]) => {
            const parts = key.split('-');
            const thisTrackName = parts.slice(0, parts.length - 4).join(' ');

            // Ensure exact track name match to prevent substring matches (e.g., "warwick" matching when looking for "warwick farm")
            if (thisTrackName.toLowerCase() === displayTrackName.toLowerCase() && 
                thisTrackName.split(' ').length === displayTrackName.split(' ').length) {
              trackStats.totalRaces++;

              // Process podium data
              if (data.podium) {
                const raceResult = {
                  raceNumber: parts[parts.length - 4],
                  date: `${parts[parts.length - 3]}-${parts[parts.length - 2]}-${parts[parts.length - 1]}`,
                  results: data.podium,
                  returns: data.returns || 0,
                  profit: data.profit || 0
                };
                trackStats.recentResults.push(raceResult);
              }

              // Process early vs race time performance
              if (data.timing === 'early') {
                trackStats.early.totalBets++;
                // Process position data...
              } else {
                trackStats.raceTime.totalBets++;
                // Process position data...
              }
            }
          });
        }

        // Sort recent results by date
        trackStats.recentResults.sort((a, b) => new Date(b.date) - new Date(a.date));

        // Calculate ROIs
        trackStats.early.roi = (trackStats.early.totalProfit / trackStats.early.totalBets) * 100;
        trackStats.raceTime.roi = (trackStats.raceTime.totalProfit / trackStats.raceTime.totalBets) * 100;

        setTrackData(trackStats);
        setIsLoading(false);
      } catch (err) {
        console.error('Error processing track data:', err);
        setError('Error processing track data');
        setIsLoading(false);
      }
    }
  }, [histResults, isHistoricalLoading, trackName, displayTrackName, trackData]);

  // Process archive data for track statistics when available
  useEffect(() => {
    if (archiveData?.data && Array.isArray(archiveData.data) && archiveData.data.length > 0) {
      try {
        // Filter out races that lack BOTH earlyPredictionData.firstFourData AND predictionData.firstFourData
        const validRaces = archiveData.data.filter((race) => {
          const hasEarlyData = race.earlyPredictionData?.firstFourData &&
            Array.isArray(race.earlyPredictionData.firstFourData) &&
            race.earlyPredictionData.firstFourData.length > 0;

          const hasPredictionData = race.predictionData?.firstFourData &&
            Array.isArray(race.predictionData.firstFourData) &&
            race.predictionData.firstFourData.length > 0;

          // Include race if it has either early data OR prediction data
          return hasEarlyData || hasPredictionData;
        });

        const trackStats = {
          name: displayTrackName,
          totalRaces: validRaces.length,
          early: {
            positions: {
              p1: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p2: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p3: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p4: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 }
            },
            totalBets: 0,
            totalReturns: 0,
            totalProfit: 0,
            roi: 0,
            racesWithAllTips: 0,
            racesWon: 0,
            winRate: 0
          },
          raceTime: {
            positions: {
              p1: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p2: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p3: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 },
              p4: { bets: 0, wins: 0, returns: 0, profit: 0, roi: 0 }
            },
            totalBets: 0,
            totalReturns: 0,
            totalProfit: 0,
            roi: 0,
            racesWithAllTips: 0,
            racesWon: 0,
            winRate: 0
          },
          recentResults: [],
          featureTipWins: {}
        };

        const featureTipWins = {};

        // Process each race in the archive data
        validRaces.forEach((race) => {
          if (!race.betfairResults?.winner) return;

          const winner = race.betfairResults.winner;
          const winnerSelectionId = winner.selectionId;
          const winPrice = parseFloat(winner.startingPrice) || 0;
          const normalizedWinnerSelectionId = normalizeSelectionId(winnerSelectionId);
          const winnerHorseNumber = extractHorseNumber(winner);
          const tipHitsForRace = new Set();

          // Process early predictions (T1-T4)
          if (race.earlyPredictionData?.firstFourData && Array.isArray(race.earlyPredictionData.firstFourData)) {
            // Check if all 4 tips are present (valid race for win-rate calculation)
            const hasAllEarlyTips = race.earlyPredictionData.firstFourData.length === 4 &&
              race.earlyPredictionData.firstFourData.every(tip => tip?.selectionId);

            let earlyRaceWon = false;

            race.earlyPredictionData.firstFourData.forEach((tip, index) => {
              if (index < 4 && tip) {
                const posKey = `p${index + 1}`;
                const position = trackStats.early.positions[posKey];
                const tipSelectionId = normalizeSelectionId(tip?.selectionId);
                const tipHorseNumber = extractHorseNumber(tip);
                const matchesWinnerById = normalizedWinnerSelectionId && tipSelectionId && tipSelectionId === normalizedWinnerSelectionId;
                const matchesWinnerByNumber = !matchesWinnerById && winnerHorseNumber && tipHorseNumber && tipHorseNumber === winnerHorseNumber;
                const isWinningTip = matchesWinnerById || matchesWinnerByNumber;

                position.bets += 1;
                trackStats.early.totalBets += 1;

                if (isWinningTip) {
                  position.wins += 1;
                  position.returns += winPrice;
                  trackStats.early.totalReturns += winPrice;
                  earlyRaceWon = true;
                  tipHitsForRace.add(`T${index + 1}`);
                } else {
                  // Lost bet - subtract $1
                  position.returns += 0;
                }

                position.profit = position.returns - position.bets;
                position.roi = position.bets > 0 ? (position.profit / position.bets) * 100 : 0;
              }
            });

            // Track win rate only for races with all 4 tips
            if (hasAllEarlyTips) {
              trackStats.early.racesWithAllTips += 1;
              if (earlyRaceWon) {
                trackStats.early.racesWon += 1;
              }
            }
          }

          // Process race time predictions (T5-T8)
          if (race.predictionData?.firstFourData && Array.isArray(race.predictionData.firstFourData)) {
            // Check if all 4 tips are present (valid race for win-rate calculation)
            const hasAllRaceTimeTips = race.predictionData.firstFourData.length === 4 &&
              race.predictionData.firstFourData.every(tip => tip?.selectionId);

            let raceTimeRaceWon = false;

            race.predictionData.firstFourData.forEach((tip, index) => {
              if (index < 4 && tip) {
                const posKey = `p${index + 1}`;
                const position = trackStats.raceTime.positions[posKey];
                const tipSelectionId = normalizeSelectionId(tip?.selectionId);
                const tipHorseNumber = extractHorseNumber(tip);
                const matchesWinnerById = normalizedWinnerSelectionId && tipSelectionId && tipSelectionId === normalizedWinnerSelectionId;
                const matchesWinnerByNumber = !matchesWinnerById && winnerHorseNumber && tipHorseNumber && tipHorseNumber === winnerHorseNumber;
                const isWinningTip = matchesWinnerById || matchesWinnerByNumber;

                position.bets += 1;
                trackStats.raceTime.totalBets += 1;

                if (isWinningTip) {
                  position.wins += 1;
                  position.returns += winPrice;
                  trackStats.raceTime.totalReturns += winPrice;
                  raceTimeRaceWon = true;
                  tipHitsForRace.add(`T${index + 5}`);
                } else {
                  // Lost bet - subtract $1
                  position.returns += 0;
                }

                position.profit = position.returns - position.bets;
                position.roi = position.bets > 0 ? (position.profit / position.bets) * 100 : 0;
              }
            });

            // Track win rate only for races with all 4 tips
            if (hasAllRaceTimeTips) {
              trackStats.raceTime.racesWithAllTips += 1;
              if (raceTimeRaceWon) {
                trackStats.raceTime.racesWon += 1;
              }
            }
          }

          const raceNumberKey = getRaceNumberFromRace(race);
          const raceDateKey = getRaceDateKey(race);
          const primaryKey = buildFeatureEventKey(raceDateKey, raceNumberKey);
          const fallbackKey = !primaryKey && raceNumberKey ? buildFeatureEventKey('unknown', raceNumberKey) : null;
          const featureKey = primaryKey || fallbackKey;

          if (featureKey && tipHitsForRace.size > 0) {
            const existing = featureTipWins[featureKey] || [];
            const combined = Array.from(new Set([...existing, ...tipHitsForRace])).sort((a, b) => {
              const aNum = parseInt(a.slice(1), 10);
              const bNum = parseInt(b.slice(1), 10);
              if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
                return a.localeCompare(b);
              }
              return aNum - bNum;
            });
            featureTipWins[featureKey] = combined;
          }
        });

        // Calculate overall totals and ROIs
        trackStats.early.totalProfit = trackStats.early.totalReturns - trackStats.early.totalBets;
        trackStats.early.roi = trackStats.early.totalBets > 0 ? (trackStats.early.totalProfit / trackStats.early.totalBets) * 100 : 0;
        trackStats.early.winRate = trackStats.early.racesWithAllTips > 0 ? (trackStats.early.racesWon / trackStats.early.racesWithAllTips) * 100 : 0;

        trackStats.raceTime.totalProfit = trackStats.raceTime.totalReturns - trackStats.raceTime.totalBets;
        trackStats.raceTime.roi = trackStats.raceTime.totalBets > 0 ? (trackStats.raceTime.totalProfit / trackStats.raceTime.totalBets) * 100 : 0;
        trackStats.raceTime.winRate = trackStats.raceTime.racesWithAllTips > 0 ? (trackStats.raceTime.racesWon / trackStats.raceTime.racesWithAllTips) * 100 : 0;

        trackStats.featureTipWins = featureTipWins;

        setTrackData(trackStats);
        setIsLoading(false);
      } catch (err) {
        console.error('Error processing archive data:', err);
        setError('Error processing archive data');
        setIsLoading(false);
      }
    }
  }, [archiveData, displayTrackName]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card className="p-4 text-center text-red-500">
          {error}
        </Card>
      </div>
    );
  }

  const stateDisplayName = {
    'qld': 'Queensland',
    'nsw': 'New South Wales',
    'vic': 'Victoria',
    'sa': 'South Australia',
    'wa': 'Western Australia',
    'tas': 'Tasmania',
    'nt': 'Northern Territory',
    'act': 'Australian Capital Territory'
  }[state.toLowerCase()];

  return (
    <>
      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
      <Helmet>
        <title>{trackData.name} Racing Results & Statistics | Lexi Tips</title>
        <meta
          name="description"
          content={`View detailed horse racing results and performance statistics for ${trackData.name}. Analysis of wins, places, ROI and historical performance data.`}
        />
        <meta
          name="keywords"
          content={`${trackData.name}, ${stateDisplayName} racing, horse racing results, track performance, racing tips, ${trackData.name} statistics`}
        />
      </Helmet>

      <div className="mt-20 mb-6">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <Link to="/analysis" className="hover:text-[#F30099] transition-colors">
            Analysis
          </Link>
          <span>/</span>
          <Link to={`/analysis/${state.toLowerCase()}`} className="hover:text-[#F30099] transition-colors">
            {stateDisplayName}
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{trackData.name}</span>
        </nav>
      </div>

      <h1 className="text-2xl font-bold mb-6">{trackData.name} Racing Results</h1>

      {/* Track Information */}
      <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-[#F30099] to-purple-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">About {trackData.name} Racecourse</h2>
              <p className="text-white/90 text-sm">Premier racing venue in {stateDisplayName}</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Track Description */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  Track Overview
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {trackDetails[trackData.name]?.description || `${trackData.name} is a premier racing venue in ${stateDisplayName}.`}
                </p>

                <ROIComparisonText
                  earlyROI={trackData.early.roi}
                  raceTimeROI={trackData.raceTime.roi}
                />

                <div className="mt-4 flex items-center gap-2">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${trackData.early.roi > trackData.raceTime.roi
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-purple-100 text-purple-800'
                    }`}>
                    {trackData.early.roi > trackData.raceTime.roi ? 'Race Day tips returned more here' : 'Late Mail tips returned more here'}
                  </div>
                </div>

                {/* Feature Events Accordion */}
                <TrackEventsAccordion
                  events={trackEvents[trackData.name] || []}
                  winningTipsByRace={trackData.featureTipWins || {}}
                />
              </div>

              {/* Data Quality Indicator */}
              {trackData.totalRaces < 50 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-800 mb-1">Limited Data Sample</h4>
                      <p className="text-amber-700 text-sm">
                        Under 50 races recorded ({trackData.totalRaces} races). Statistics may not be fully representative of long-term performance.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-green-800 mb-1">Robust Data Sample</h4>
                      <p className="text-green-700 text-sm">
                        Comprehensive analysis based on {trackData.totalRaces} races. Statistics provide reliable performance insights.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Performance Insights */}
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 border border-gray-100 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  Performance Insights
                </h3>

                <div className="space-y-4">
                  {/* Early Tips Performance */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-blue-900 flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                        Early Tips (T1-T4)
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${trackData.early.roi > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {trackData.early.roi.toFixed(1)}% ROI
                      </span>
                    </div>
                    <p className="text-blue-800 text-sm mb-2">
                      {trackData.early.totalBets.toLocaleString()} $1 bets, net result {trackData.early.roi > 0 ? 'positive' : 'negative'}
                    </p>
                    {trackData.early.racesWithAllTips > 0 && (
                      <div className="flex items-center gap-2 pt-2 border-t border-blue-200">
                        <span className="text-blue-700 text-xs font-medium">Win Rate:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${trackData.early.winRate > 50 ? 'bg-green-100 text-green-800' : trackData.early.winRate > 25 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {trackData.early.winRate.toFixed(1)}%
                        </span>
                        <span className="text-blue-600 text-xs">
                          ({trackData.early.racesWon}/{trackData.early.racesWithAllTips} races)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Race Time Tips Performance */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-purple-900 flex items-center gap-2">
                        <div className="w-4 h-4 bg-purple-500 rounded-full"></div>
                        Race Time Tips (T5-T8)
                      </h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${trackData.raceTime.roi > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {trackData.raceTime.roi.toFixed(1)}% ROI
                      </span>
                    </div>
                    <p className="text-purple-800 text-sm mb-2">
                      {trackData.raceTime.totalBets.toLocaleString()} $1 bets, net result {trackData.raceTime.roi > 0 ? 'positive' : 'negative'}
                    </p>
                    {trackData.raceTime.racesWithAllTips > 0 && (
                      <div className="flex items-center gap-2 pt-2 border-t border-purple-200">
                        <span className="text-purple-700 text-xs font-medium">Win Rate:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${trackData.raceTime.winRate > 50 ? 'bg-green-100 text-green-800' : trackData.raceTime.winRate > 25 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {trackData.raceTime.winRate.toFixed(1)}%
                        </span>
                        <span className="text-purple-600 text-xs">
                          ({trackData.raceTime.racesWon}/{trackData.raceTime.racesWithAllTips} races)
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Best Strategy Recommendation */}
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-medium text-yellow-800 mb-1">At this track</h4>
                        <p className="text-yellow-700 text-sm">
                          {trackData.early.roi > trackData.raceTime.roi ? 'Race Day tips' : 'Late Mail tips'} have returned more here so far. Past results only, not a prediction.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Track Overview - Hero Section */}
        <div className="relative overflow-hidden rounded-xl bg-white border border-[#F30099]/20 shadow-md">
          <div className="relative p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-[#F30099]/10 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-[#F30099]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{trackData.name}</h2>
                  <p className="text-gray-500">Track Performance Overview</p>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${Math.max(trackData.early.roi, trackData.raceTime.roi) > 20 ? 'bg-green-100 text-green-800' :
                Math.max(trackData.early.roi, trackData.raceTime.roi) > 0 ? 'bg-amber-100 text-amber-800' :
                  'bg-red-100 text-red-800'
                }`}>
                {Math.max(trackData.early.roi, trackData.raceTime.roi) > 20 ? '🔥 Hot Track' :
                  Math.max(trackData.early.roi, trackData.raceTime.roi) > 0 ? '📈 Positive Returns' :
                    '📉 Needs Improvement'}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F30099]/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#F30099]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <span className="text-gray-500 text-sm">Total Races Tipped</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {Math.max(
                    trackData.early.totalBets / 4,
                    trackData.raceTime.totalBets / 4
                  ).toLocaleString()}
                </div>
                <div className="text-gray-500 text-xs space-y-0.5">
                  <div>{(trackData.early.totalBets / 4).toLocaleString()} morning, {(trackData.raceTime.totalBets / 4).toLocaleString()} race-time</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F30099]/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#F30099]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                  <span className="text-gray-500 text-sm">Best Tip</span>
                </div>
                <div className="text-lg font-bold text-gray-900 mb-1">Early Tip 1</div>
                <div className="text-gray-500 text-sm">Highest ROI Performance</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <span className="text-gray-500 text-sm">Best ROI</span>
                </div>
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {Math.max(trackData.early.roi, trackData.raceTime.roi).toLocaleString(undefined, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  })}%
                </div>
                <div className="text-gray-500 text-sm">Return on Investment</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F30099]/10 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#F30099]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-gray-500 text-sm">Net Returns</span>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  ${((trackData.early.totalReturns + trackData.raceTime.totalReturns) -
                    (trackData.early.totalBets + trackData.raceTime.totalBets)).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                </div>
                <div className="text-gray-500 text-sm">Returns After Bets</div>
              </div>
            </div>

            {/* Max Hedge Controls */}
            {SHOW_PROFIT_AND_HEDGE && (
            <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showMaxHedge}
                    onChange={(e) => setShowMaxHedge(e.target.checked)}
                    className="text-[#F30099] focus:ring-[#F30099]"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Max Hedge Analysis</span>
                </label>
                {showMaxHedge && (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700">Win Rate Threshold:</label>
                      <Input
                        type="number"
                        value={maxHedgeWinRate}
                        onChange={(e) => setMaxHedgeWinRate(parseFloat(e.target.value) || 0.8)}
                        className="w-20 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#F30099] focus:border-transparent text-sm"
                        step="0.1"
                        min="0"
                        max="1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="showMaxHedgeColumns"
                        checked={showMaxHedge}
                        onChange={(e) => setShowMaxHedge(e.target.checked)}
                        className="w-4 h-4 text-[#F30099] bg-gray-100 border-gray-300 rounded focus:ring-[#F30099] focus:ring-2"
                      />
                      <label htmlFor="showMaxHedgeColumns" className="text-sm font-medium text-gray-700">
                        Show Max Hedge columns in table
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {showMaxHedge && trackWithMaxHedge && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-100 rounded-lg p-4 border border-blue-200">
                    <h5 className="font-medium text-blue-900 mb-2">Morning Max Hedge (T1-T4)</h5>
                    <div className="text-2xl font-bold text-blue-800 mb-1">
                      {trackWithMaxHedge.morningMaxHedge?.toFixed(3) || '0.000'}
                    </div>
                    <div className={`text-sm ${trackWithMaxHedge.morningMaxHedge < maxHedgeWinRate && trackWithMaxHedge.morningMaxHedge > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {trackWithMaxHedge.morningMaxHedge < maxHedgeWinRate && trackWithMaxHedge.morningMaxHedge > 0 ? '✓ Qualifies' : '✗ Does not qualify'}
                    </div>
                  </div>

                  <div className="bg-purple-100 rounded-lg p-4 border border-purple-200">
                    <h5 className="font-medium text-purple-900 mb-2">Race-time Max Hedge (T5-T8)</h5>
                    <div className="text-2xl font-bold text-purple-800 mb-1">
                      {trackWithMaxHedge.raceTimeMaxHedge?.toFixed(3) || '0.000'}
                    </div>
                    <div className={`text-sm ${trackWithMaxHedge.raceTimeMaxHedge < maxHedgeWinRate && trackWithMaxHedge.raceTimeMaxHedge > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {trackWithMaxHedge.raceTimeMaxHedge < maxHedgeWinRate && trackWithMaxHedge.raceTimeMaxHedge > 0 ? '✓ Qualifies' : '✗ Does not qualify'}
                    </div>
                  </div>

                  <div className="bg-green-100 rounded-lg p-4 border border-green-200">
                    <h5 className="font-medium text-green-900 mb-2">Combined Max Hedge</h5>
                    <div className="text-2xl font-bold text-green-800 mb-1">
                      {trackWithMaxHedge.combinedMaxHedge?.toFixed(3) || '0.000'}
                    </div>
                    <div className={`text-sm ${trackWithMaxHedge.combinedMaxHedge < maxHedgeWinRate && trackWithMaxHedge.combinedMaxHedge > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {trackWithMaxHedge.combinedMaxHedge < maxHedgeWinRate && trackWithMaxHedge.combinedMaxHedge > 0 ? '✓ Qualifies' : '✗ Does not qualify'}
                    </div>
                    {maxHedgeSummary.qualifyingTracks > 0 && (
                      <div className="text-xs text-green-700 mt-1">
                        Potential return: ${maxHedgeSummary.totalWon.toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showMaxHedge && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h6 className="font-medium text-yellow-800 mb-2">Max Hedge Explanation</h6>
                  <p className="text-sm text-yellow-700">
                    Max hedge values represent the sum of reciprocals (1/odds) for each tip position.
                    Races below the threshold are the ones the max hedge example would have backed. Past results only, not a prediction.
                    Combined max hedge counts each different horse once, using the Late Mail odds where a horse is in both.
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Individual Tip Line Performance */}
            <div className="mt-8">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Individual Tip Line Performance</h4>
                <p className="text-sm text-gray-600">Performance metrics for each tip position (T1-T8)</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                {/* Early Tips (T1-T4) */}
                {Object.entries(trackData.early.positions).map(([pos, stats], index) => {
                  const tipNumber = index + 1;
                  const winRate = stats.bets > 0 ? (stats.wins / stats.bets) * 100 : 0;
                  const netProfit = stats.returns - stats.bets;

                  return (
                    <div key={`early-${pos}`} className="bg-blue-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">T{tipNumber}</span>
                        </div>
                        <span className="text-xs text-blue-600 font-medium">Early</span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">ROI:</span>
                          <span className={`text-sm font-bold ${stats.roi > 0 ? 'text-green-600' : stats.roi < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                            {stats.roi.toFixed(1)}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Win Rate:</span>
                          <span className={`text-sm font-bold ${winRate >= 20 ? 'text-green-600' : winRate >= 10 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                            {winRate.toFixed(1)}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Bets:</span>
                          <span className="text-sm font-medium text-gray-900">{stats.bets}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Wins:</span>
                          <span className="text-sm font-medium text-gray-900">{stats.wins}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Win Total:</span>
                          <span className="text-sm font-medium text-green-600">
                            ${stats.returns.toFixed(2)}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Net:</span>
                          <span className={`text-sm font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {netProfit >= 0 ? `+$${netProfit.toFixed(2)}` : `-$${Math.abs(netProfit).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Race Time Tips (T5-T8) */}
                {Object.entries(trackData.raceTime.positions).map(([pos, stats], index) => {
                  const tipNumber = index + 5;
                  const winRate = stats.bets > 0 ? (stats.wins / stats.bets) * 100 : 0;
                  const netProfit = stats.returns - stats.bets;

                  return (
                    <div key={`racetime-${pos}`} className="bg-purple-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-white">T{tipNumber}</span>
                        </div>
                        <span className="text-xs text-purple-600 font-medium">Race</span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">ROI:</span>
                          <span className={`text-sm font-bold ${stats.roi > 0 ? 'text-green-600' : stats.roi < 0 ? 'text-red-600' : 'text-gray-600'
                            }`}>
                            {stats.roi.toFixed(1)}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Win Rate:</span>
                          <span className={`text-sm font-bold ${winRate >= 20 ? 'text-green-600' : winRate >= 10 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                            {winRate.toFixed(1)}%
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Bets:</span>
                          <span className="text-sm font-medium text-gray-900">{stats.bets}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Wins:</span>
                          <span className="text-sm font-medium text-gray-900">{stats.wins}</span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Win Total:</span>
                          <span className="text-sm font-medium text-green-600">
                            ${stats.returns.toFixed(2)}
                          </span>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Net:</span>
                          <span className={`text-sm font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {netProfit >= 0 ? `+$${netProfit.toFixed(2)}` : `-$${Math.abs(netProfit).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>






        {/* Betting Strategies Section */}
        {SHOW_PROFIT_AND_HEDGE && (isAuthenticated ? (
          <BettingStrategies
            trackPerformanceData={{ data: [{ models: { HayiAuto: trackData } }] }}
            historicalData={archiveData}
            maxHedgeSummary={maxHedgeSummary}
            maxHedgeWinRate={maxHedgeWinRate}
            setMaxHedgeWinRate={setMaxHedgeWinRate}
            optimalThresholds={optimalThresholds}
          />
        ) : (
          <div className="relative">
            {/* Blurred Betting Strategies */}
            <div className="filter blur-sm pointer-events-none">
              <BettingStrategies
                trackPerformanceData={{ data: [{ models: { HayiAuto: trackData } }] }}
                historicalData={{ data: {} }}
                maxHedgeSummary={{
                  morning: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 },
                  raceTime: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 },
                  combined: { qualifyingRaces: 0, totalWon: 0, totalWagered: 0, netReturn: 0, roi: 0 }
                }}
                maxHedgeWinRate={0.8}
                setMaxHedgeWinRate={() => { }}
                optimalThresholds={{
                  morning: { threshold: 0.80, roi: 0, races: 0 },
                  raceTime: { threshold: 0.80, roi: 0, races: 0 },
                  combined: { threshold: 0.80, roi: 0, races: 0 }
                }}
              />
            </div>

            {/* CTA Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center bg-white/95 p-6 rounded-lg shadow-lg border border-[#F30099]/20 max-w-md">
                <div className="mb-4 flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-[#F30099]/10 flex items-center justify-center">
                    <LockIcon className="w-6 h-6 text-[#F30099]" />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Unlock Betting Strategies</h4>
                <p className="text-gray-600 mb-4">
                  Sign up to access detailed betting strategy analysis and performance metrics.
                </p>
                <button
                  onClick={() => setIsAuthModalOpen(true)}
                  className="inline-block bg-[#F30099] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#F30099]/90 transition-colors"
                >
                  Get Access Now
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Results Archive Table */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 xs:p-8 shadow-lg hover:shadow-xl transition-all duration-300 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Historical Results Archive</h3>
            {isAuthenticated && (
              <div className="text-sm text-gray-500">

                {trackData.early.totalBets && trackData.raceTime.totalBets ? `${Math.max(
                  trackData.early.totalBets / 4,
                  trackData.raceTime.totalBets / 4
                ).toLocaleString()} races found` : 'Loading...'}
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <ResultsArchiveTable
              archiveData={archiveData}
              isLoading={isArchiveLoading}
              error={archiveError}
              trackName={trackData?.name}
              showMaxHedge={showMaxHedge}
              setShowMaxHedge={setShowMaxHedge}
              maxHedgeWinRate={maxHedgeWinRate}
              setMaxHedgeWinRate={setMaxHedgeWinRate}
            />
          ) : (
            <div className="relative">
              {/* Blurred Skeleton */}
              <div className="filter blur-sm pointer-events-none">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="p-4 space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex space-x-4">
                        <div className="w-24 h-6 bg-gray-200 rounded"></div>
                        <div className="flex-1 h-6 bg-gray-200 rounded"></div>
                        <div className="w-32 h-6 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center bg-white/95 p-6 rounded-lg shadow-lg border border-[#F30099]/20 max-w-md">
                  <div className="mb-4 flex justify-center">
                    <div className="w-12 h-12 rounded-full bg-[#F30099]/10 flex items-center justify-center">
                      <LockIcon className="w-6 h-6 text-[#F30099]" />
                    </div>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Get Access to Historical Results</h4>
                  <p className="text-gray-600 mb-4">
                    Sign up now to unlock complete access to historical racing results and performance data.
                  </p>
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="inline-block bg-[#F30099] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#F30099]/90 transition-colors"
                  >
                    Get Tips Now
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>


      </div>

    </>
  );
}